import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Archive, Loader2, RotateCcw } from 'lucide-react';
import { backups } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

const fmtDate = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const statusChip = (status) => (status === 'Success'
  ? { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)', label: 'ok' }
  : { background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', label: 'falhou' });

const apiMessage = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

/**
 * Cópias de segurança de UMA instância: última cópia, «Fazer cópia agora», lista das últimas,
 * configuração (ligada/desligada e retenção) e a reposição — que exige o nome da instância
 * escrito à mão, porque é a única ação aqui que escreve por cima dos dados de um ginásio.
 *
 * O servidor tira sempre uma cópia do estado actual antes de repor e aborta se essa cópia
 * falhar; este ecrã di-lo em voz alta antes de deixar confirmar.
 */
const BackupsPanel = ({ instanceId, instanceName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [retention, setRetention] = useState(30);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [restoreError, setRestoreError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await backups.detail(instanceId);
      setData(res.data);
      setEnabled(!!res.data?.enabled);
      setRetention(res.data?.retentionDays || 30);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler as cópias desta instância.'));
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.backups || [];
  const last = rows[0] || null;

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await backups.now(instanceId);
      toast.success(res.data?.message || 'Cópia concluída.');
      await load();
    } catch (e) {
      // Um backup falhado devolve 422 com o motivo; a linha falhada aparece na lista ao lado.
      toast.error(apiMessage(e, 'A cópia falhou.'));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await backups.saveSettings(instanceId, { enabled, retentionDays: Number(retention) });
      toast.success(res.data?.message || 'Configuração guardada.');
      await load();
    } catch (e) {
      toast.error(apiMessage(e, 'Não foi possível guardar a configuração.'));
    } finally {
      setSaving(false);
    }
  };

  const openRestore = (row) => {
    setRestoring(row);
    setConfirmation('');
    setRestoreError(null);
  };

  const confirmationOk = !!restoring
    && confirmation.trim().length > 0
    && confirmation.trim().toLowerCase() === (instanceName || '').trim().toLowerCase();

  const confirmRestore = async () => {
    if (!restoring || !confirmationOk) return;
    setBusy(true);
    setRestoreError(null);
    try {
      const res = await backups.restore(instanceId, restoring.id, confirmation.trim());
      toast.success(res.data?.message || 'Cópia reposta.');
      setRestoring(null);
      await load();
    } catch (e) {
      setRestoreError(apiMessage(e, 'A reposição não foi feita.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="backups-panel" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <Archive size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Cópias de segurança</h3>
        </div>
        <button
          type="button"
          data-testid="backup-now"
          onClick={runNow}
          disabled={busy || loading}
          style={{ ...styles.primary, opacity: busy || loading ? 0.6 : 1 }}
        >
          {busy ? 'A copiar…' : 'Fazer cópia agora'}
        </button>
      </div>

      {loading && (
        <div style={styles.muted}><Loader2 size={16} className="spin" /> A ler as cópias…</div>
      )}

      {!loading && loadError && (
        <div style={styles.errorBox}>
          <div>{loadError}</div>
          <button type="button" onClick={load} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div data-testid="last-backup" style={styles.lastLine}>
            <strong>Última cópia:</strong>{' '}
            {last ? (
              <>
                {fmtDate(last.createdAt)} · {last.kindLabel} · {fmtSize(last.sizeBytes)} ·{' '}
                <span style={{ ...styles.chip, ...statusChip(last.status) }}>{statusChip(last.status).label}</span>
              </>
            ) : (
              'nunca foi copiada'
            )}
          </div>

          {last && last.status !== 'Success' && last.message && (
            <div style={styles.errorBox} data-testid="last-backup-reason">
              A última cópia falhou: {last.message}
            </div>
          )}

          {/* Configuração */}
          <div style={styles.settings}>
            <label htmlFor={`backup-enabled-${instanceId}`} style={styles.checkLabel}>
              <input
                id={`backup-enabled-${instanceId}`}
                data-testid="backup-enabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                style={styles.checkbox}
              />
              <span>Cópia automática diária</span>
            </label>

            <label htmlFor={`backup-retention-${instanceId}`} style={styles.inlineField}>
              <span>Retenção (dias)</span>
              <input
                id={`backup-retention-${instanceId}`}
                data-testid="backup-retention"
                type="number"
                min={data?.minRetentionDays || 1}
                max={data?.maxRetentionDays || 365}
                value={retention}
                onChange={(e) => setRetention(e.target.value)}
                style={styles.number}
              />
            </label>

            <button
              type="button"
              data-testid="backup-save-settings"
              onClick={saveSettings}
              disabled={saving}
              style={{ ...styles.secondary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
          </div>

          <div style={styles.pathNote}>
            Ficheiros no servidor: <code style={styles.code}>{data?.rootDirectory}</code>/&lt;contentor&gt;/
          </div>

          {rows.length === 0 ? (
            <div style={styles.muted}>Ainda não há cópias desta instância.</div>
          ) : (
            <div style={{ marginTop: '0.75rem' }}>
              {rows.map((row) => (
                <div key={row.id} data-testid={`backup-row-${row.id}`} style={styles.row}>
                  <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ ...styles.chip, ...statusChip(row.status) }}>{statusChip(row.status).label}</span>
                      <span style={styles.rowTitle}>{fmtDate(row.createdAt)}</span>
                      <span style={styles.mutedSmall}>{row.kindLabel} · {fmtSize(row.sizeBytes)}</span>
                      {row.restoredAt && (
                        <span style={styles.mutedSmall}>reposta em {fmtDate(row.restoredAt)}</span>
                      )}
                    </div>
                    <div style={styles.pathLine}>{row.fileName}</div>
                    {row.status !== 'Success' && row.message && (
                      <div style={{ ...styles.errorText, marginTop: '2px' }}>{row.message}</div>
                    )}
                  </div>

                  {row.status === 'Success' && (
                    <button
                      type="button"
                      data-testid={`restore-open-${row.id}`}
                      onClick={() => openRestore(row)}
                      style={styles.danger}
                    >
                      <RotateCcw size={14} /> Repor
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Barreira de confirmação escrita */}
      {restoring && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Repor cópia de segurança">
          <div style={styles.dialog}>
            <h4 style={styles.title}>Repor a cópia de {fmtDate(restoring.createdAt)}?</h4>
            <p style={styles.warning}>
              Isto substitui os dados actuais de <strong>{instanceName}</strong> pelos desta cópia.
              Antes de repor, o servidor tira uma cópia do estado actual; se essa cópia falhar, a
              reposição é abortada e nada é alterado.
            </p>
            <label htmlFor="restore-confirmation" style={styles.label}>
              Escreva <strong>{instanceName}</strong> para confirmar
            </label>
            <input
              id="restore-confirmation"
              data-testid="restore-confirmation"
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              style={styles.input}
            />

            {restoreError && <div style={styles.errorBox}>{restoreError}</div>}

            <div style={styles.actions}>
              <button
                type="button"
                onClick={() => setRestoring(null)}
                disabled={busy}
                style={styles.secondary}
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="restore-submit"
                onClick={confirmRestore}
                disabled={!confirmationOk || busy}
                style={{ ...styles.danger, opacity: !confirmationOk || busy ? 0.5 : 1 }}
              >
                {busy ? 'A repor…' : 'Repor agora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '0.75rem', flexWrap: 'wrap',
  },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  primary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer',
  },
  secondary: {
    minHeight: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  danger: {
    minHeight: TAP, padding: '0 1rem', borderRadius: '10px', border: '1px solid var(--error-text, #a11616)',
    background: 'transparent', color: 'var(--error-text, #a11616)', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
  },
  lastLine: { marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: 1.6 },
  muted: {
    marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  mutedSmall: { fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  errorText: { fontSize: '0.75rem', color: 'var(--error-text, #a11616)' },
  settings: {
    marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap',
  },
  checkLabel: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: TAP,
    fontSize: '0.875rem', fontWeight: 600,
  },
  checkbox: { width: '24px', height: '24px' },
  inlineField: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: TAP, fontSize: '0.875rem',
  },
  number: {
    width: '84px', minHeight: TAP, padding: '0 0.5rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '0.9rem',
  },
  pathNote: { marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted, #6b6b6b)', wordBreak: 'break-word' },
  code: { fontFamily: 'monospace', fontSize: '0.78rem' },
  row: {
    display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', padding: '0.6rem 0', borderTop: '1px solid var(--border, #eee)',
  },
  rowTitle: { fontSize: '0.85rem', fontWeight: 600 },
  pathLine: {
    fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)',
    wordBreak: 'break-all', marginTop: '2px',
  },
  chip: { padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000,
  },
  dialog: {
    background: 'var(--bg-secondary, #fff)', color: 'var(--text-primary, #1c1c1c)',
    borderRadius: '16px', padding: '1.25rem', width: '100%', maxWidth: '520px', maxHeight: '90vh',
    overflowY: 'auto',
  },
  warning: { fontSize: '0.85rem', lineHeight: 1.6, margin: '0.75rem 0' },
  label: { display: 'block', fontSize: '0.85rem', marginBottom: '0.35rem' },
  input: {
    width: '100%', minHeight: TAP, padding: '0 0.75rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '1rem',
  },
  actions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem', flexWrap: 'wrap' },
};

export default BackupsPanel;
